import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "@/frontend/auth-client";
import { cn } from "@/frontend/lib/utils";
import type {
  HealthSystemOption,
  HealthSystemSection,
} from "@/frontend/types/dashboard/health-systems";
import { MEDICARE_SOURCE_TYPE } from "@/frontend/types/fhir";
import { MedicareConsentDialog } from "@/frontend/components/shared/MedicareConsentDialog";
import { HealthSystemPicker } from "@/frontend/components/dashboard/patient/HealthSystemPicker";
import { ConnectedHealthSystems } from "@/frontend/components/dashboard/patient/ConnectedHealthSystems";
import {
  IndividualConnectRail,
  useConnectionsNeedingSignIn,
} from "./IndividualConnectRail";
import {
  useDefaultSuggestedSourcesQuery,
  usePatientAccountsQuery,
} from "@/frontend/queries/dashboard/patients";

type ConnectHealthSystemsProps = {
  /** The owner's own `patient_org_membership.id`, from `org.ensureSelfPatient`. */
  patientMembershipId: string;
  /** Active workspace, required by the SMART link endpoint. */
  organizationId: string | null;
  /** Named in the consent copy. */
  organizationName: string | null;
};

/**
 * The connect surface for an individual workspace, where the owner is the
 * patient. Picking a health system hands them straight to its sign-in screen,
 * which returns here. Below sits the list of systems already connected, and
 * beside both a rail for anything needing a fresh sign-in.
 */
export function ConnectHealthSystems({
  patientMembershipId,
  organizationId,
  organizationName,
}: ConnectHealthSystemsProps) {
  const [pendingSource, setPendingSource] = useState<HealthSystemOption | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Medicare cannot hand off until CMS Blue Button 3b.2 has been shown, so the
  // pick waits here rather than in `pendingSource`, which redirects on mount.
  const [medicareSource, setMedicareSource] = useState<HealthSystemOption | null>(null);
  // A ref, not the state flag: the handoff panel's ref callback runs twice under
  // StrictMode, and `isRedirecting` is stale in the second closure. Two starts
  // meant two `link/smart` calls, and the first one's redirect aborted the
  // second's fetch, which surfaced as "NetworkError when attempting to fetch".
  const oauthStarted = useRef(false);
  const { data: accounts } = usePatientAccountsQuery(patientMembershipId);
  const { data: defaults, isLoading: defaultsLoading } = useDefaultSuggestedSourcesQuery();
  // The rail is the only thing in the second column, so an empty rail means
  // there is no second column and the picker takes the whole width.
  const needSignIn = useConnectionsNeedingSignIn(patientMembershipId);

  const addedUrls = new Set(
    (accounts ?? [])
      .filter((account) => account.connection_status === "connected")
      .map((account) => account.providerId),
  );

  // Popular choices minus anything already connected, so the list only offers
  // systems that are still worth a click.
  const popular = (defaults?.sources ?? []).filter(
    (source) => !addedUrls.has(source.fhirBaseUrl),
  );
  const sections: HealthSystemSection[] = [
    { id: "popular-health-systems", label: "Popular choices", sources: popular },
  ];

  /**
   * Hands the browser to the health system's SMART authorization screen. The
   * callback returns to this exact page, so a completed connection lands back
   * on the list it came from.
   */
  const startOauth = async (source: HealthSystemOption) => {
    if (!organizationId) {
      toast.error("Your workspace is still loading. Try again in a moment.");
      return;
    }
    if (oauthStarted.current) return;
    oauthStarted.current = true;
    setIsRedirecting(true);
    try {
      await authClient.link.smart({
        fhirBaseUrl: source.fhirBaseUrl,
        organizationId,
        callbackURL: window.location.href,
      });
    } catch (error) {
      oauthStarted.current = false;
      setIsRedirecting(false);
      setPendingSource(null);
      // The endpoint's own message names the missing piece: an unconfigured
      // client id, an unknown source, a workspace that may not connect.
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : `Could not start the connection to ${source.name}`,
      );
    }
  };

  /**
   * Straight to the health system's own sign-in, except for Medicare, which owes
   * the reader its CMS disclosure first.
   */
  const handleSelect = (source: HealthSystemOption) => {
    oauthStarted.current = false;
    if (source.type === MEDICARE_SOURCE_TYPE) {
      setMedicareSource(source);
      return;
    }
    setPendingSource(source);
  };

  /** Takes the Medicare disclosure as agreed and starts the handoff. */
  const confirmMedicare = () => {
    const source = medicareSource;
    setMedicareSource(null);
    if (source) setPendingSource(source);
  };

  /**
   * Fires the pending redirect as soon as the handoff panel mounts. A ref
   * callback rather than an effect, and the panel mounts once per pick, so the
   * OAuth call cannot repeat.
   */
  const redirectOnMount = (node: HTMLDivElement | null) => {
    if (!node || !pendingSource) return;
    void startOauth(pendingSource);
  };

  if (pendingSource) {
    return (
      <section
        ref={redirectOnMount}
        className="flex flex-col items-center justify-center rounded-lg border bg-card px-6 py-16 text-center"
      >
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
        <p className="mt-3 text-body font-medium">Taking you to {pendingSource.name}</p>
        <p className="mt-1 text-caption text-muted-foreground">
          Sign in there with your patient portal details.
        </p>
      </section>
    );
  }

  return (
    <div
      className={cn(
        "grid items-start gap-5",
        needSignIn.length > 0 && "lg:grid-cols-[minmax(0,1fr)_20rem]",
      )}
    >
      <div className="flex min-w-0 flex-col gap-4">
        <HealthSystemPicker
          title="Connect your health systems"
          hint="Pick a hospital, clinic or insurer, then sign in with the patient portal details you already use."
          sections={sections}
          addedUrls={addedUrls}
          onSelect={handleSelect}
          action="connect"
          isLoading={defaultsLoading}
        />

        <ConnectedHealthSystems patientId={patientMembershipId} />
      </div>

      <IndividualConnectRail needSignIn={needSignIn} organizationId={organizationId} />

      {/* `self`, since an individual workspace holds only the owner's own
          records and no other party receives them. */}
      <MedicareConsentDialog
        open={!!medicareSource}
        onOpenChange={(open) => {
          if (!open) setMedicareSource(null);
        }}
        recipient={{ kind: "self" }}
        onConfirm={confirmMedicare}
        isBusy={!organizationId}
      />
    </div>
  );
}
